import { FC, KeyboardEvent, useCallback, useMemo, memo, useState, useEffect, useRef } from 'react'
import { ComposeId, ComposeCommand, ComposeState } from 'interfaces/feed'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import { ComposeInput } from 'components/features/socialFeed/ComposeInput/ComposeInput'
import { ComposeDecorations } from 'components/features/socialFeed/ComposeDecorations/ComposeDecorations'
import styles from 'components/features/socialFeed/ComposeInputController/ComposeInputController.styl'
import cn from 'classnames'
import {
  feedComposeTypeTextActionCreator,
  composeExecCommandActionCreator,
  feedComposeAddImageAttemptActionCreator,
  composeValidateActionCreator,
  FeedComposeTypeTextPayload,
} from 'actions/feedComposeActions'
import { useDebouncedCallback } from 'use-debounce'
import { SUGGESTS_KEYS_TO_COMMAND_MAP } from 'components/features/socialFeed/ComposeMentionsSuggests/ComposeMentionsSuggests'
import { useComposeCommand } from 'helpers/composeHooks'
import { checkTextareaSafari3pxBug } from 'helpers/DOMHelpers'
import { useComposeState } from 'helpers/hookHelpers'

const DEFAULT_COMPOSE = {
  text: null,
  isEdit: null,
}
type HandleTextChangeCallback = (nextText: string) => void

type ComposeInputControllerProps = {
  composeId: ComposeId
}

export const ComposeInputController: FC<ComposeInputControllerProps> = memo((props) => {
  const { composeId } = props

  const dispatch = useStoreDispatch()

  const composeState = useComposeState<Partial<Omit<ComposeState, 'text' | 'isEdit'>> & { text: ComposeState['text'] | null, isEdit: ComposeState['isEdit'] | null }>(composeId, DEFAULT_COMPOSE)
  const {
    text: composeText,
    isEdit,
    type: composeType,
    inputBehavior,
  } = composeState

  const [selection, setSelection] = useState({
    selectionStart: 0,
    selectionEnd: 0,
  })

  // text state and handling
  const [text, setText] = useState(composeText)

  const [dispatchTextChangeDebounced, , flushDispatchTextChangeDebounced] = useDebouncedCallback((params: FeedComposeTypeTextPayload) => {
    dispatch(feedComposeTypeTextActionCreator(params))
  }, 500, { maxWait: 1000 })
  useEffect(() => flushDispatchTextChangeDebounced, [flushDispatchTextChangeDebounced])

  const handleTextChange = useCallback<HandleTextChangeCallback>((nextText) => {
    if (nextText !== text) {
      setText(nextText)
      dispatchTextChangeDebounced({ text: nextText, composeId })
    }
  }, [text, setText, dispatchTextChangeDebounced, composeId])

  // effect bellow should invoke `handleTextChange` on composeText change only
  // but anyways it have to invoke latest copy of `handleTextChange` callback
  // so to prevent invokes on `handleTextChange` change let's pass object that have callback reference instead callback itself
  const handleTextChangeRef = useRef<HandleTextChangeCallback>()
  handleTextChangeRef.current = handleTextChange
  useEffect(() => {
    if (composeText === null || !handleTextChangeRef.current) {
      return
    }
    handleTextChangeRef.current(composeText)
  }, [composeText, handleTextChangeRef])

  // submit handling
  const handleSubmit = useCallback(() => {
    dispatch(composeValidateActionCreator.request({
      composeId,
    }))
  }, [composeId, dispatch])

  useComposeCommand(props, {
    submit: handleSubmit,
  })

  // keys handling
  const handleKeyDown = useCallback<(event: KeyboardEvent<HTMLDivElement>) => void>((event) => {
    if (inputBehavior === 'suggest') {
      const command = SUGGESTS_KEYS_TO_COMMAND_MAP[event.key]
      if (!command) {
        return
      }

      event.preventDefault()
      dispatch(composeExecCommandActionCreator({
        composeId,
        command: {
          type: command,
        } as ComposeCommand,
      }))

    } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      handleSubmit()
    }
  }, [inputBehavior, composeId, dispatch, handleSubmit])

  const [fixSafari, setFixSafari] = useState(false)
  useEffect(() => {
    checkTextareaSafari3pxBug().then((isCorrect) => {
      if (!isCorrect) {
        setFixSafari(true)
      }
    })
  }, [])

  // paste files handling
  const handlePastedFiles = useCallback<(files: File[]) => void>((files) => {
    if (!files || files.length === 0) {
      return
    }
    dispatch(feedComposeAddImageAttemptActionCreator.request({ files, composeId }))
  }, [composeId, dispatch])

  const className = useMemo(() => cn(
    styles.composeInputController,
    {
      [styles.safariIos]: fixSafari,
    }
  ), [fixSafari])

  if (isEdit === null || composeType === null || text === null) {
    return null
  }

  return (
    <div
      onKeyDownCapture={ handleKeyDown }
      className={ className }
    >
      <div className={ styles.input }>
        <ComposeInput
          composeId={ composeId }

          value={ text }
          onChange={ handleTextChange }
          onSelectionChange={ setSelection }

          onPastedFiles={ handlePastedFiles }
        />
      </div>
      <div className={ styles.decorations }>
        <ComposeDecorations
          composeId={ composeId }
          text={ text }
          selection={ selection }
        />
      </div>
    </div>
  )
})
